import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, X, Edit2, Key, ShieldOff, ShieldCheck, ClipboardCopy, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ROLES = ["admin", "manager", "staff"];

function rolePill(role) {
  const m = { admin: "bg-emerald-50 text-emerald-700", manager: "bg-indigo-50 text-indigo-700", staff: "bg-slate-100 text-slate-700" };
  return m[role] || "bg-slate-100 text-slate-700";
}

function genTempPassword(len = 12) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#%";
  let p = "";
  for (let i = 0; i < len; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p;
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const blankForm = { name: "", email: "", password: "", role: "staff", job_title: "", phone: "" };
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [resetPwd, setResetPwd] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, must_change_password: true });
      toast.success("User created — they will be prompted to change the password on first login");
      setForm(blankForm);
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: editing.name, email: editing.email, role: editing.role, is_active: editing.is_active, phone: editing.phone || "", job_title: editing.job_title || "" };
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this user? All their staff records will be removed too.")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const doReset = async () => {
    if (resetPwd.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.patch(`/users/${resetTarget.id}`, { password: resetPwd });
      toast.success(`Password reset for ${resetTarget.email}. They will be prompted to change it on next login.`);
      setResetTarget(null);
      setResetPwd("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copied to clipboard");
  };

  if (me?.role !== "admin") {
    return <div className="p-10 text-slate-500">Only admins can manage users.</div>;
  }

  return (
    <div className="p-8 lg:p-10" data-testid="page-admin-users">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Team</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-500 mt-2">Admins manage all access. New users receive a temporary password and are forced to set their own on first login.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/audit-log" className="border border-slate-300 hover:border-slate-900 text-slate-700 font-semibold px-5 py-2.5 flex items-center gap-2 text-sm" data-testid="audit-log-link">
            <FileText className="w-4 h-4" /> Audit log
          </Link>
          <button onClick={() => setShowNew(true)} className="gs-gradient text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:opacity-90" data-testid="new-user-btn">
            <Plus className="w-4 h-4" /> New user
          </button>
        </div>
      </div>

      {users === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Email</th>
                <th className="px-6 py-3 font-medium">Role</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium w-56">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-4 font-medium text-slate-900">
                    <Link to={`/admin/staff/${u.id}`} className="hover:underline" data-testid={`staff-link-${u.id}`}>{u.name}</Link>
                    {u.must_change_password && <span className="ml-2 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5">pwd reset pending</span>}
                  </td>
                  <td className="px-6 py-4 text-slate-600">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${rolePill(u.role)}`}>{u.role}</span>
                  </td>
                  <td className="px-6 py-4">
                    {u.is_active === false ? <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-red-50 text-red-700">disabled</span> : <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-emerald-50 text-emerald-700">active</span>}
                  </td>
                  <td className="px-6 py-4 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 flex gap-2">
                    <button title="Edit" onClick={() => setEditing({ ...u })} className="text-slate-600 hover:text-slate-900" data-testid={`edit-user-${u.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button title="Reset password" onClick={() => { setResetTarget(u); setResetPwd(genTempPassword()); }} className="text-slate-600 hover:text-amber-700" data-testid={`reset-user-${u.id}`}><Key className="w-4 h-4" /></button>
                    {u.id !== me.id && (
                      u.is_active !== false ? (
                        <button title="Disable" onClick={async () => { await api.patch(`/users/${u.id}`, { is_active: false }); load(); toast.success("User disabled"); }} className="text-slate-600 hover:text-red-700" data-testid={`disable-user-${u.id}`}><ShieldOff className="w-4 h-4" /></button>
                      ) : (
                        <button title="Re-enable" onClick={async () => { await api.patch(`/users/${u.id}`, { is_active: true }); load(); toast.success("User enabled"); }} className="text-slate-600 hover:text-emerald-700" data-testid={`enable-user-${u.id}`}><ShieldCheck className="w-4 h-4" /></button>
                      )
                    )}
                    {u.id !== me.id && (
                      <button title="Delete" onClick={() => remove(u.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <Modal title="Add team member" onClose={() => setShowNew(false)} testid="new-user-modal">
          <form onSubmit={create} className="space-y-4">
            <Field label="Full name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ipt" data-testid="new-user-name" /></Field>
            <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ipt" data-testid="new-user-email" /></Field>
            <Field label="Job title (optional)"><input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="ipt" data-testid="new-user-job" /></Field>
            <Field label="Role"><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="ipt" data-testid="new-user-role">{ROLES.map(r => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}</select></Field>
            <Field label="Temporary password">
              <div className="flex gap-2">
                <input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="ipt flex-1" data-testid="new-user-password" />
                <button type="button" onClick={() => setForm({ ...form, password: genTempPassword() })} className="border border-slate-300 px-3 text-xs hover:border-slate-900 whitespace-nowrap" data-testid="new-user-genpw">Generate</button>
                {form.password && <button type="button" onClick={() => copy(form.password)} className="border border-slate-300 px-3 hover:border-slate-900" data-testid="new-user-copypw"><ClipboardCopy className="w-4 h-4" /></button>}
              </div>
              <p className="text-xs text-slate-500 mt-1">User will be required to change this on their first login.</p>
            </Field>
            <button type="submit" disabled={saving} className="gs-gradient text-white font-bold px-6 py-3 w-full mt-2 hover:opacity-90 disabled:opacity-50" data-testid="new-user-submit">{saving ? "Creating…" : "Create user"}</button>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit ${editing.name}`} onClose={() => setEditing(null)} testid="edit-user-modal">
          <form onSubmit={saveEdit} className="space-y-4">
            <Field label="Full name"><input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="ipt" data-testid="edit-user-name" /></Field>
            <Field label="Email"><input required type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="ipt" data-testid="edit-user-email" /></Field>
            <Field label="Job title"><input value={editing.job_title || ""} onChange={(e) => setEditing({ ...editing, job_title: e.target.value })} className="ipt" /></Field>
            <Field label="Phone"><input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="ipt" /></Field>
            <Field label="Role"><select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="ipt">{ROLES.map(r => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}</select></Field>
            <Field label="Active">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                Account is enabled
              </label>
            </Field>
            <button type="submit" disabled={saving} className="gs-gradient text-white font-bold px-6 py-3 w-full mt-2 hover:opacity-90 disabled:opacity-50" data-testid="edit-user-submit">{saving ? "Saving…" : "Save changes"}</button>
          </form>
        </Modal>
      )}

      {resetTarget && (
        <Modal title={`Reset password for ${resetTarget.name}`} onClose={() => { setResetTarget(null); setResetPwd(""); }} testid="reset-pwd-modal">
          <div className="space-y-4">
            <p className="text-sm text-slate-600">A new temporary password will be set. The user will be required to change it on their next login.</p>
            <Field label="New temporary password">
              <div className="flex gap-2">
                <input minLength={8} value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="ipt flex-1" data-testid="reset-pwd-input" />
                <button type="button" onClick={() => setResetPwd(genTempPassword())} className="border border-slate-300 px-3 text-xs hover:border-slate-900 whitespace-nowrap">Generate</button>
                <button type="button" onClick={() => copy(resetPwd)} className="border border-slate-300 px-3 hover:border-slate-900"><ClipboardCopy className="w-4 h-4" /></button>
              </div>
            </Field>
            <button onClick={doReset} className="gs-gradient text-white font-bold px-6 py-3 w-full mt-2 hover:opacity-90" data-testid="reset-pwd-submit">Reset password</button>
          </div>
        </Modal>
      )}

      <style>{`.ipt { width: 100%; border: 1px solid rgb(203, 213, 225); padding: 0.75rem 1rem; outline: none; } .ipt:focus { border-color: #1FA59A; }`}</style>
    </div>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h2 className="font-display text-2xl font-bold">{title}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">{label}</label>{children}</div>;
}
